<template>
  <DynamicActionsMenu
    :actions="actions"
    :options="menuOptions"
    @action="handleAction"
  >
    <template #trigger>
      <slot name="trigger">
        <EllipsisIcon class="tooltip-trigger" width="20" />
      </slot>
    </template>
    <template #footer>
      <slot name="footer" />
    </template>
  </DynamicActionsMenu>
</template>

<script setup lang="ts">
import { computed } from "vue";

import DynamicActionsMenu from "@/components/DynamicActionsMenu.vue";
import EllipsisIcon from "@/images/dots-horizontal-outline.svg?component";
import type { Invoice } from "@/typings/invoice";
import type { InvoiceMenuAction, MenuAction, TippyOptions } from "@/typings/ui";
import { InvoiceAction } from "@/typings/ui";

const props = defineProps<{
  invoice: Invoice;
  tippyOptions?: TippyOptions;
  visibleActions?: InvoiceAction[];
}>();

const emit = defineEmits<{
  (e: "invoice-action", payload: { id: number; action: InvoiceAction }): void;
}>();

const menuOptions = computed<TippyOptions>(() => ({
  theme: "light",
  appendTo: document.body,
  ...props.tippyOptions
}));

const fullyPaid = computed<boolean>(() => {
  const inv = props.invoice;
  const spb = inv.progress_billing;
  if (spb?.is_progress_billing) {
    return (spb.total_balance_remaining_cents ?? 0) <= 0;
  }
  return (inv.financials?.balance_cents ?? 0) <= 0;
});

const allActions = computed<InvoiceMenuAction[]>(() => {
  const inv = props.invoice;
  const links = inv.links || ({} as NonNullable<Invoice["links"]>);

  const base: InvoiceMenuAction[] = [
    {
      key: InvoiceAction.Approve,
      label: "Approve Invoice",
      icon: "fa-regular fa-thumbs-up",
      eventName: "approve-invoice",
      className: "menu-item",
      link: links.invoice_approve
    },
    {
      key: InvoiceAction.AcceptPayment,
      label: "Accept Payment",
      icon: "fa-regular fa-credit-card",
      eventName: "accept-payment",
      className: "menu-item",
      link: links.payment_create,
      disabled: !links.payment_create || fullyPaid.value
    },
    {
      key: InvoiceAction.IssueRefund,
      label: "Issue Refund",
      icon: "fa-regular fa-money-check-edit-alt",
      eventName: "issue-refund",
      className: "menu-item",
      link: links.invoice_refund
    },
    {
      key: InvoiceAction.EditDetails,
      label: "Edit Details",
      icon: "fa-regular fa-calendar-lines-pen",
      eventName: "edit-details",
      className: "menu-item",
      link: links.update_details
    },
    {
      key: InvoiceAction.EditDate,
      label: "Edit Date",
      icon: "fa-regular fa-calendar-lines-pen",
      eventName: "edit-date",
      className: "menu-item",
      link: links.edit_invoice_date,
      tooltip: !links.edit_invoice_date
        ? "Invoice must be approved before date can be edited."
        : ""
    },
    {
      key: InvoiceAction.Unapprove,
      label: "Edit Invoice",
      icon: "far fa-pen-to-square",
      eventName: "unapprove-invoice",
      className: "menu-item",
      link: links.invoice_break
    },
    {
      key: InvoiceAction.Void,
      label: "Void Invoice",
      icon: "fa-regular fa-ban",
      eventName: "void-invoice",
      className: "menu-item",
      link: links.void
    },
    ...(inv.exclude_from_batching
      ? [
          {
            key: InvoiceAction.ResumeBatch,
            label: "Resume Batching",
            icon: "fa-regular fa-badge-check",
            eventName: "resume-batch",
            className: "menu-item",
            link: links.update_batching
          } as InvoiceMenuAction
        ]
      : [
          {
            key: InvoiceAction.DoNotBatch,
            label: "Do Not Batch",
            icon: "fa-regular fa-file-circle-xmark",
            eventName: "do-not-batch",
            className: "menu-item",
            link: links.update_batching
          } as InvoiceMenuAction
        ]),
    {
      key: InvoiceAction.Download,
      label: "Download Invoice",
      icon: "fa-regular fa-download",
      eventName: "download",
      className: "menu-item",
      link: links.invoice_pdf
    },
    {
      key: InvoiceAction.Resubmit,
      label: "Resubmit for Payment",
      icon: "fa-regular fa-credit-card",
      eventName: "resubmit-invoice",
      className: "menu-item",
      link: links.resubmit
    },
    {
      key: InvoiceAction.Email,
      label: "Email",
      icon: "fa-regular fa-envelope",
      eventName: "email-invoice",
      className: "menu-item",
      link: links.invoice_email
    }
  ];

  return base;
});

const actions = computed<InvoiceMenuAction[]>(() => {
  if (!props.visibleActions) return allActions.value;
  return allActions.value.filter((a) => props.visibleActions!.includes(a.key));
});

function handleAction(action: MenuAction) {
  const invoiceAction = action as InvoiceMenuAction;
  emit("invoice-action", {
    id: props.invoice.invoice_id,
    action: invoiceAction.key
  });
}
</script>

<style lang="scss" scoped>
.tooltip-trigger {
  text-align: center;
  cursor: pointer;
}
</style>
